import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { RefreshCw, Lock, AlertCircle, Clock, Plus, X, Search, UserPlus, Filter, GripVertical, CheckCircle, ChevronDown, MessageCircle, FolderOpen, DollarSign, Info, Tag, Phone, MapPin, Mail, Smartphone, MessageSquare, Minus, Flame, Zap, Paperclip, UploadCloud, FileText, Check, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { setoresAPI, atendimentosAPI, beneficiariosAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import NovaTarefaModal from '../components/NovaTarefaModal';

const Kanban = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [setores, setSetores] = useState([]);
  const [activeSetor, setActiveSetor] = useState(null);
  const [colunas, setColunas] = useState([]);
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filtros, setFiltros] = useState({
    protocolo: '',
    nome: '',
    cpf: '',
    status: ''
  });
  const [showFiltros, setShowFiltros] = useState(false);

  // Estados do modal de novo atendimento
  const [showModal, setShowModal] = useState(false);
  const [showTarefaModal, setShowTarefaModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBeneficiario, setSelectedBeneficiario] = useState(null);
  const [showNewBeneficiario, setShowNewBeneficiario] = useState(false);
  const [saving, setSaving] = useState(false);

  // Formulário de novo atendimento
  const [formData, setFormData] = useState({
    motivo: '',
    prioridade: 'normal',
    canal_entrada: 'telefonico',
    categoria_id: '',
    tipo_id: '',
    subtipo_id: '',
    setor_id: '',
    campos_extras: {},
  });

  // Categorias, tipos e subtipos
  const [categorias, setCategorias] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [subtipos, setSubtipos] = useState([]);
  const [camposDinamicos, setCamposDinamicos] = useState([]);

  // Cofre de documentos do beneficiario (T7/T8) — operam por CPF
  const [tiposDoc, setTiposDoc] = useState([]);
  const [docsExistentes, setDocsExistentes] = useState([]);
  const [anexos, setAnexos] = useState([]); // novos: {uid, file, tipoId, status, docId}
  const [dragOver, setDragOver] = useState(false);
  const anexoUid = React.useRef(0);

  // Formulário de novo beneficiário
  const [newBeneficiario, setNewBeneficiario] = useState({
    nome_completo: '',
    cpf: '',
    telefone_principal: '',
    email: '',
  });

  useEffect(() => {
    loadSetores();
  }, []);

  useEffect(() => {
    if (activeSetor) {
      loadKanbanData(activeSetor.id);
    }
  }, [activeSetor, filtros]);

  // Busca de beneficiários com debounce
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const delay = setTimeout(() => {
        searchBeneficiarios(searchTerm);
      }, 300);
      return () => clearTimeout(delay);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  // Carregar categorias, tipos e subtipos
  useEffect(() => {
    const loadClassificacao = async () => {
      try {
        const [catRes, tipoRes, subtipoRes] = await Promise.all([
          api.get('/config-atendimentos/categorias'),
          api.get('/config-atendimentos/tipos'),
          api.get('/config-atendimentos/subtipos'),
        ]);
        setCategorias(catRes.data);
        setTipos(tipoRes.data);
        setSubtipos(subtipoRes.data);
      } catch (error) {
        console.error('Erro ao carregar classificação:', error);
      }
    };
    loadClassificacao();
  }, []);

  // Abrir modal automaticamente quando vem da tela de beneficiário
  useEffect(() => {
    if (location.state?.abrirNovoAtendimento && setores.length > 0) {
      const benef = location.state.beneficiario;
      setShowModal(true);
      if (benef) {
        setSelectedBeneficiario(benef);
      }
      setSearchTerm('');
      setSearchResults([]);
      setShowNewBeneficiario(false);
      const setorDefault = activeSetor ? activeSetor.id.toString() : '';
      setFormData({ motivo: '', prioridade: 'normal', canal_entrada: 'telefonico', categoria_id: '', tipo_id: '', subtipo_id: '', setor_id: setorDefault, campos_extras: {} });
      setCamposDinamicos([]);
      if (setorDefault) loadCamposDinamicos(setorDefault);
      // Limpar state para não reabrir ao voltar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, setores]);

  const loadSetores = async () => {
    try {
      const response = await setoresAPI.list();
      let lista = response.data;

      // Filtrar setores por permissão do usuário (Kanban sempre filtra pelos setores do usuario)
      if (user && user.perfil !== 'administrador') {
        const userSetorIds = user.setor_ids || [];
        if (userSetorIds.length > 0) {
          lista = lista.filter(s => userSetorIds.includes(s.id));
        }
      }

      setSetores(lista);
      if (lista.length > 0) {
        setActiveSetor(lista[0]);
        // Carrega cards imediatamente — não confiar só no efeito [activeSetor],
        // que pode perder a primeira mudança de null→setor em StrictMode/remount.
        loadKanbanData(lista[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar setores');
    }
    setLoading(false);
  };

  const loadKanbanData = async (setorId, tentativa = 1) => {
    setLoading(true);
    try {
      const [colunasRes, cardsRes] = await Promise.all([
        setoresAPI.getColunas(setorId),
        atendimentosAPI.getKanban(setorId, filtros),
      ]);
      setColunas(colunasRes.data);

      const cardsPorColuna = {};
      colunasRes.data.forEach((col) => {
        cardsPorColuna[col.id] = [];
      });
      cardsRes.data.forEach((card) => {
        if (cardsPorColuna[card.coluna_id]) {
          cardsPorColuna[card.coluna_id].push(card);
        }
      });
      setCards(cardsPorColuna);
    } catch (error) {
      if (error.response?.status === 403) {
        // sem acesso — não retentar
      } else if (tentativa < 3) {
        // retry automático com backoff (cold start / pool aquecendo)
        setTimeout(() => loadKanbanData(setorId, tentativa + 1), tentativa * 800);
        return;
      } else {
        toast.error('Erro ao carregar Kanban');
      }
    }
    setLoading(false);
  };

  const handleLimparFiltros = () => {
    setFiltros({
      protocolo: '',
      nome: '',
      cpf: '',
      status: ''
    });
  };

  const searchBeneficiarios = async (termo) => {
    try {
      const response = await beneficiariosAPI.search(termo);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId, type } = result;

    // Se está reordenando colunas
    if (type === 'COLUMN') {
      if (source.index === destination.index) return;

      const newColunas = Array.from(colunas);
      const [removed] = newColunas.splice(source.index, 1);
      newColunas.splice(destination.index, 0, removed);
      setColunas(newColunas);

      try {
        const colunaIds = newColunas.map(c => c.id);
        await setoresAPI.reordenarColunas(activeSetor.id, colunaIds);
        toast.success('Colunas reordenadas com sucesso');
      } catch (error) {
        toast.error('Erro ao reordenar colunas');
        loadKanbanData(activeSetor.id);
      }
      return;
    }

    // Movendo cards entre colunas
    if (source.droppableId === destination.droppableId) return;

    // Finalizar não é por arraste: o botão Finalizar registra o retorno do
    // cliente e só então dispara a pesquisa de satisfação.
    const colunaDestino = colunas.find((c) => c.id.toString() === destination.droppableId);
    if (colunaDestino?.eh_final) {
      toast('Para finalizar, abra o atendimento e use o botão Finalizar.', { icon: 'ℹ️' });
      return;
    }

    const newCards = { ...cards };
    const card = newCards[source.droppableId].find((c) => c.id.toString() === draggableId);
    newCards[source.droppableId] = newCards[source.droppableId].filter(
      (c) => c.id.toString() !== draggableId
    );
    newCards[destination.droppableId] = [
      ...newCards[destination.droppableId],
      { ...card, coluna_id: parseInt(destination.droppableId) },
    ];
    setCards(newCards);

    try {
      await atendimentosAPI.mover(parseInt(draggableId), parseInt(destination.droppableId));
      toast.success('Card movido com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover card');
      loadKanbanData(activeSetor.id);
    }
  };

  const formatTempo = (minutos) => {
    if (minutos < 60) return `${minutos}min`;
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas < 24) return `${horas}h ${mins}min`;
    const dias = Math.floor(horas / 24);
    return `${dias}d ${horas % 24}h`;
  };

  const handleCardClick = (cardId) => {
    window.location.href = `/atendimento/${cardId}`;
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedBeneficiario(null);
    setSearchTerm('');
    setSearchResults([]);
    setShowNewBeneficiario(false);
    const setorDefault = activeSetor ? activeSetor.id.toString() : '';
    setFormData({ motivo: '', prioridade: 'normal', canal_entrada: 'telefonico', categoria_id: '', tipo_id: '', subtipo_id: '', setor_id: setorDefault, campos_extras: {} });
    setCamposDinamicos([]);
    if (setorDefault) loadCamposDinamicos(setorDefault);
    setNewBeneficiario({ nome_completo: '', cpf: '', telefone_principal: '', email: '' });
    setAnexos([]);
    setDocsExistentes([]);
    carregarTiposDoc();
  };

  const handleSelectBeneficiario = (benef) => {
    setSelectedBeneficiario(benef);
    setSearchResults([]);
    setSearchTerm('');
    setAnexos([]);
    carregarDocsBeneficiario(benef.cpf);
  };

  // ===== Cofre de documentos do beneficiario (T7/T8) — por CPF =====
  const carregarTiposDoc = async () => {
    try {
      const res = await api.get('/documentos/tipos', { params: { categoria: 'beneficiario' } });
      setTiposDoc(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar tipos de documento:', e);
    }
  };

  const carregarDocsBeneficiario = async (cpf) => {
    const cpfLimpo = String(cpf || '').replace(/\D/g, '');
    if (!cpfLimpo) { setDocsExistentes([]); return; }
    try {
      const res = await api.get(`/documentos/beneficiario/${cpfLimpo}`);
      setDocsExistentes(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar documentos do beneficiario:', e);
      setDocsExistentes([]);
    }
  };

  const adicionarArquivos = (fileList) => {
    const novos = Array.from(fileList || []).map((file) => ({
      uid: ++anexoUid.current,
      file,
      tipoId: '',
      status: 'pendente',
      docId: null,
    }));
    if (novos.length) setAnexos((prev) => [...prev, ...novos]);
  };

  const removerAnexoPendente = (uid) =>
    setAnexos((prev) => prev.filter((a) => a.uid !== uid));

  // Ao escolher o tipo (obrigatorio), o arquivo sobe direto pro cofre da pessoa
  const classificarEEnviar = async (uid, tipoId) => {
    const cpfLimpo = String(selectedBeneficiario?.cpf || '').replace(/\D/g, '');
    const alvo = anexos.find((a) => a.uid === uid);
    if (!alvo || !cpfLimpo) return;
    setAnexos((prev) => prev.map((a) => (a.uid === uid ? { ...a, tipoId, status: 'enviando' } : a)));
    try {
      const fd = new FormData();
      fd.append('file', alvo.file);
      fd.append('tipo_documento_id', tipoId);
      const res = await api.post(`/documentos/beneficiario/${cpfLimpo}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnexos((prev) => prev.map((a) => (a.uid === uid ? { ...a, status: 'enviado', docId: res.data.id } : a)));
      toast.success('Documento anexado ao beneficiário');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao anexar documento');
      setAnexos((prev) => prev.map((a) => (a.uid === uid ? { ...a, status: 'erro', tipoId: '' } : a)));
    }
  };

  const removerAnexoEnviado = async (uid, docId) => {
    const cpfLimpo = String(selectedBeneficiario?.cpf || '').replace(/\D/g, '');
    try {
      await api.delete(`/documentos/beneficiario/${cpfLimpo}/${docId}`);
      setAnexos((prev) => prev.filter((a) => a.uid !== uid));
      toast.success('Documento removido');
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  const iconBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0.25rem', borderRadius: '6px', border: 'none',
    background: 'transparent', color: '#94a3b8', cursor: 'pointer',
  };

  const handleCreateBeneficiario = async () => {
    if (!newBeneficiario.nome_completo || !newBeneficiario.cpf) {
      toast.error('Nome e CPF são obrigatórios');
      return;
    }

    try {
      const response = await beneficiariosAPI.create({
        ...newBeneficiario,
        cpf: newBeneficiario.cpf.replace(/\D/g, ''),
      });
      setSelectedBeneficiario(response.data);
      setShowNewBeneficiario(false);
      setAnexos([]);
      carregarDocsBeneficiario(response.data.cpf);
      toast.success('Beneficiário cadastrado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar beneficiário');
    }
  };

  const loadCamposDinamicos = async (setorId) => {
    try {
      const response = await api.get(`/config-atendimentos/setor/${setorId}/campos-ativos`);
      setCamposDinamicos(response.data);
      const extras = {};
      response.data.forEach(campo => { extras[campo.nome] = ''; });
      setFormData(prev => ({ ...prev, campos_extras: extras }));
    } catch (error) {
      setCamposDinamicos([]);
    }
  };

  const handleSetorChangeModal = (e) => {
    const setorId = e.target.value;
    setFormData(prev => ({
      ...prev,
      setor_id: setorId,
      categoria_id: '',
      tipo_id: '',
      subtipo_id: '',
      campos_extras: {}
    }));
    if (setorId) loadCamposDinamicos(setorId);
    else setCamposDinamicos([]);
  };

  const handleCampoExtraChange = (nomeCampo, valor) => {
    setFormData(prev => ({
      ...prev,
      campos_extras: { ...prev.campos_extras, [nomeCampo]: valor }
    }));
  };

  const renderCampoDinamico = (campo) => {
    const valor = formData.campos_extras[campo.nome] || '';
    switch (campo.tipo) {
      case 'textarea':
        return <textarea className="form-control" rows={3} value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.value)} placeholder={campo.placeholder} />;
      case 'numero':
        return <input type="number" className="form-control" value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.value)} placeholder={campo.placeholder} />;
      case 'moeda':
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem', color: 'var(--gray-500)' }}>R$</span>
            <input type="number" step="0.01" className="form-control" value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, parseFloat(e.target.value) || '')} placeholder={campo.placeholder || '0,00'} />
          </div>
        );
      case 'data':
        return <input type="date" className="form-control" value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.value)} />;
      case 'select':
        return (
          <select className="form-control" value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.value)}>
            <option value="">Selecione...</option>
            {campo.opcoes?.map((op, i) => <option key={i} value={op}>{op}</option>)}
          </select>
        );
      case 'checkbox':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={valor === true || valor === 'true'} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.checked)} />
            {campo.label}
          </label>
        );
      default:
        return <input type="text" className="form-control" value={valor} onChange={(e) => handleCampoExtraChange(campo.nome, e.target.value)} placeholder={campo.placeholder} />;
    }
  };

  const handleCreateAtendimento = async (finalizar = false) => {
    if (!selectedBeneficiario) {
      toast.error('Selecione ou cadastre um beneficiário');
      return;
    }
    if (!formData.setor_id) {
      toast.error('Selecione um setor');
      return;
    }
    if (!formData.motivo) {
      toast.error('Informe o motivo do atendimento');
      return;
    }
    // Só exige o que existe para escolher: sem categoria cadastrada (ou categoria
    // sem tipos) a tela escondia o campo e mesmo assim barrava o atendimento.
    if (categorias.length > 0 && !formData.categoria_id) {
      toast.error('Selecione uma categoria');
      return;
    }
    const tiposDaCategoria = tipos.filter((t) => t.categoria_id === parseInt(formData.categoria_id));
    if (tiposDaCategoria.length > 0 && !formData.tipo_id) {
      toast.error('Selecione um tipo');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        setor_id: parseInt(formData.setor_id),
        motivo: formData.motivo,
        prioridade: formData.prioridade,
        canal_entrada: formData.canal_entrada,
        categoria_id: formData.categoria_id ? parseInt(formData.categoria_id) : null,
        tipo_id: formData.tipo_id ? parseInt(formData.tipo_id) : null,
        subtipo_id: formData.subtipo_id ? parseInt(formData.subtipo_id) : null,
        campos_extras: formData.campos_extras,
        enviar_kanban: true,
      };
      if (selectedBeneficiario.id) {
        payload.beneficiario_id = selectedBeneficiario.id;
      } else {
        payload.cpf = selectedBeneficiario.cpf;
      }
      const response = await atendimentosAPI.create(payload);

      if (finalizar) {
        await atendimentosAPI.finalizar(response.data.id, formData.motivo);
        toast.success(`Atendimento finalizado! Protocolo: ${response.data.protocolo}`);
      } else {
        toast.success(`Atendimento criado! Protocolo: ${response.data.protocolo}`);
      }

      setShowModal(false);
      setFormData({
        motivo: '',
        prioridade: 'normal',
        canal_entrada: 'telefonico',
        categoria_id: '',
        tipo_id: '',
        subtipo_id: '',
        setor_id: '',
        campos_extras: {},
      });
      setCamposDinamicos([]);
      if (response.data?.id) {
        navigate(`/atendimento/${response.data.id}`);
      } else {
        loadKanbanData(activeSetor.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar atendimento');
    }
    setSaving(false);
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  if (loading && !activeSetor) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="kanban-container">
      {/* Header */}
      <div className="kanban-header">
        <div className="kanban-tabs">
          {setores.map((setor) => (
            <button
              key={setor.id}
              className={`kanban-tab ${activeSetor?.id === setor.id ? 'active' : ''}`}
              onClick={() => setActiveSetor(setor)}
            >
              {setor.cadeado && <Lock size={14} style={{ marginRight: '4px' }} />}
              {setor.nome}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={`btn ${showFiltros ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFiltros(!showFiltros)}
          >
            <Filter size={16} />
            Filtros
          </button>
          <button className="btn btn-success" onClick={handleOpenModal}>
            <Plus size={18} />
            Novo Atendimento
          </button>
          <button className="btn btn-secondary" onClick={() => setShowTarefaModal(true)} title="Trabalho interno do setor, sem contato com beneficiário">
            <CheckCircle size={16} />
            Nova tarefa
          </button>
          <NovaTarefaModal
            aberto={showTarefaModal}
            onClose={() => setShowTarefaModal(false)}
            setores={setores}
            setorInicial={activeSetor?.id}
            onCriada={() => activeSetor && loadKanbanData(activeSetor.id)}
          />
          <button
            className="btn btn-secondary"
            onClick={() => loadKanbanData(activeSetor.id)}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Painel de Filtros */}
      {showFiltros && (
        <div className="search-box" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.85rem' }}>Protocolo</label>
              <input
                type="text"
                className="form-control"
                placeholder="Protocolo (20 dígitos)"
                value={filtros.protocolo}
                onChange={(e) => setFiltros({ ...filtros, protocolo: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.85rem' }}>Nome</label>
              <input
                type="text"
                className="form-control"
                placeholder="Nome do beneficiário"
                value={filtros.nome}
                onChange={(e) => setFiltros({ ...filtros, nome: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.85rem' }}>CPF</label>
              <input
                type="text"
                className="form-control"
                placeholder="000.000.000-00"
                value={filtros.cpf}
                onChange={(e) => setFiltros({ ...filtros, cpf: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.85rem' }}>Status</label>
              <select
                className="form-control"
                value={filtros.status}
                onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="aberto">Aberto</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="aguardando_retorno">Aguardando Retorno</option>
                <option value="aguardando_outro_setor">Aguardando Outro Setor</option>
                <option value="transferido">Transferido</option>
              </select>
            </div>
            <button
              className="btn btn-secondary"
              onClick={handleLimparFiltros}
              style={{ height: 'fit-content' }}
            >
              <X size={16} />
              Limpar
            </button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="board" type="COLUMN" direction="horizontal">
          {(provided) => (
            <div className="kanban-board" ref={provided.innerRef} {...provided.droppableProps}>
              {colunas.map((coluna, index) => (
                <Draggable
                  key={coluna.id}
                  draggableId={`column-${coluna.id}`}
                  index={index}
                  isDragDisabled={user?.perfil !== 'administrador'}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`kanban-column ${snapshot.isDragging ? 'is-dragging' : ''}`}
                      style={{
                        ...provided.draggableProps.style,
                      }}
                    >
                      <div className="column-header">
                        {user?.perfil === 'administrador' && (
                          <div {...provided.dragHandleProps} className="column-drag-handle">
                            <GripVertical size={16} />
                          </div>
                        )}
                        <span>{coluna.nome}</span>
                        <span className="column-count">{cards[coluna.id]?.length || 0}</span>
                      </div>
                      <Droppable droppableId={coluna.id.toString()}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="column-cards"
                            style={{
                              background: snapshot.isDraggingOver ? 'var(--gray-200)' : 'transparent',
                            }}
                          >
                            {cards[coluna.id]?.map((card, index) => (
                              <Draggable
                                key={card.id}
                                draggableId={card.id.toString()}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`kanban-card priority-${card.prioridade}`}
                                    onClick={() => handleCardClick(card.id)}
                                    style={{
                                      ...provided.draggableProps.style,
                                      opacity: snapshot.isDragging ? 0.9 : 1,
                                    }}
                                  >
                                    <div className="card-protocolo">{card.protocolo}</div>
                                    {card.natureza === 'tarefa' ? (
                                      <div className="card-nome" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em', background: '#eef2f7', color: '#2c3e50', borderRadius: 4, padding: '1px 5px' }}>TAREFA</span>
                                        <span>{card.tipo_tarefa_nome || 'Tarefa'}{card.beneficiario_nome ? ` · ${card.beneficiario_nome}` : ''}</span>
                                      </div>
                                    ) : (
                                      <div className="card-nome">{card.beneficiario_nome}</div>
                                    )}
                                    <div className="card-motivo">{card.motivo_resumido}</div>
                                    <div className="card-footer">
                                      <span style={!card.atendente_nome ? { color: '#bdc3c7', fontStyle: 'italic' } : undefined}>
                                        {card.atendente_nome || 'Sem dono'}
                                      </span>
                                      <div className="card-icons">
                                        {card.tem_whatsapp && <MessageCircle size={12} style={{ color: '#25D366' }} />}
                                        {card.cadeado && <Lock size={12} />}
                                        {card.eh_transferido && <AlertCircle size={12} />}
                                        <span className={`sla-indicator sla-${card.sla_status}`} />
                                        <Clock size={12} />
                                        {formatTempo(card.tempo_decorrido_minutos)}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Modal Novo Atendimento */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Novo Atendimento</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            {!selectedBeneficiario ? (
              <div>
                {/* Busca de Beneficiário */}
                <div className="form-group">
                  <label>Buscar Beneficiário (CPF ou Nome)</label>
                  <div style={{ position: 'relative' }}>
                    <Search
                      size={18}
                      style={{ position: 'absolute', left: '12px', top: '12px', color: '#999' }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      style={{ paddingLeft: '40px' }}
                      placeholder="Digite para buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Resultados da Busca */}
                {searchResults.length > 0 && (
                  <div className="search-results-modal">
                    {searchResults.map((b, idx) => (
                      <div
                        key={b.id || `search-${idx}`}
                        className="search-result-item"
                        onClick={() => handleSelectBeneficiario(b)}
                      >
                        <strong>{b.nome_completo}</strong>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          CPF: {b.cpf_mascarado} {b.numero_matricula && `| Mat: ${b.numero_matricula}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Botão para cadastrar novo */}
                {searchTerm.length >= 2 && searchResults.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>
                      Nenhum beneficiário encontrado
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowNewBeneficiario(true)}
                    >
                      <UserPlus size={16} />
                      Cadastrar Novo Beneficiário
                    </button>
                  </div>
                )}

                {/* Formulário de novo beneficiário */}
                {showNewBeneficiario && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f6fa', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '1rem' }}>Cadastrar Beneficiário</h4>
                    <div className="form-group">
                      <label>Nome Completo *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newBeneficiario.nome_completo}
                        onChange={(e) =>
                          setNewBeneficiario({ ...newBeneficiario, nome_completo: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>CPF *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newBeneficiario.cpf}
                        onChange={(e) =>
                          setNewBeneficiario({ ...newBeneficiario, cpf: formatCPF(e.target.value) })
                        }
                        maxLength="14"
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefone</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newBeneficiario.telefone_principal}
                        onChange={(e) =>
                          setNewBeneficiario({ ...newBeneficiario, telefone_principal: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={newBeneficiario.email}
                        onChange={(e) =>
                          setNewBeneficiario({ ...newBeneficiario, email: e.target.value })
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowNewBeneficiario(false)}
                      >
                        Cancelar
                      </button>
                      <button className="btn btn-success" onClick={handleCreateBeneficiario}>
                        Cadastrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Beneficiário selecionado */}
                <div
                  style={{
                    background: '#e8f5e9',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <strong>{selectedBeneficiario.nome_completo}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      {selectedBeneficiario.cpf_mascarado || selectedBeneficiario.cpf}
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem' }}
                    onClick={() => setSelectedBeneficiario(null)}
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Setor Destino */}
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Setor Destino *</label>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                    {setores.map((setor) => {
                      const sel = formData.setor_id === String(setor.id);
                      return (
                        <button key={setor.id} type="button"
                          onClick={() => handleSetorChangeModal({ target: { value: String(setor.id) } })}
                          style={{
                            padding: '0.4rem 0.85rem', borderRadius: '8px', fontSize: '0.83rem',
                            fontWeight: sel ? 600 : 400,
                            border: sel ? '2px solid var(--primary,#2563eb)' : '2px solid #e2e8f0',
                            background: sel ? 'var(--primary,#2563eb)' : '#fff',
                            color: sel ? '#fff' : '#475569',
                            cursor: 'pointer', transition: 'all 0.15s',
                            boxShadow: sel ? '0 2px 8px rgba(37,99,235,0.15)' : 'none',
                          }}
                        >
                          {setor.nome}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Formulário do atendimento */}
                <div className="form-group">
                  <label>Motivo do Atendimento *</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.motivo}
                    onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                    placeholder="Descreva o motivo do atendimento de forma clara e objetiva. Este texto sera visivel ao beneficiario."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Prioridade — chips coloridos */}
                  <div className="form-group">
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Prioridade</label>
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.35rem' }}>
                      {[
                        { value: 'normal',  label: 'Normal',  Icon: Minus, bg: '#f1f5f9', border: '#cbd5e1', color: '#475569', selBg: '#e2e8f0', selBorder: '#64748b', selColor: '#1e293b' },
                        { value: 'alta',    label: 'Alta',    Icon: Flame, bg: '#fff7ed', border: '#fed7aa', color: '#ea580c', selBg: '#ea580c', selBorder: '#ea580c', selColor: '#fff' },
                        { value: 'urgente', label: 'Urgente', Icon: Zap,   bg: '#fff1f2', border: '#fecdd3', color: '#e11d48', selBg: '#e11d48', selBorder: '#e11d48', selColor: '#fff' },
                      ].map(({ value, label, Icon, bg, border, color, selBg, selBorder, selColor }) => {
                        const sel = formData.prioridade === value;
                        return (
                          <button key={value} type="button"
                            onClick={() => setFormData({ ...formData, prioridade: value })}
                            style={{
                              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                              padding: '0.4rem 0', borderRadius: '8px', fontSize: '0.82rem', fontWeight: sel ? 600 : 400,
                              border: `2px solid ${sel ? selBorder : border}`,
                              background: sel ? selBg : bg, color: sel ? selColor : color,
                              cursor: 'pointer', transition: 'all 0.15s',
                            }}
                          >
                            <Icon size={13} /> {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Canal — chips com ícone */}
                  <div className="form-group">
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Canal</label>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                      {[
                        { value: 'telefonico', label: 'Telefônico', Icon: Phone },
                        { value: 'presencial',  label: 'Presencial',  Icon: MapPin },
                        { value: 'email',       label: 'E-mail',      Icon: Mail },
                        { value: 'app',         label: 'App',         Icon: Smartphone },
                        { value: 'chat',        label: 'Chat',        Icon: MessageSquare },
                        { value: 'whatsapp',    label: 'WhatsApp',    Icon: MessageCircle },
                      ].map(({ value, label, Icon }) => {
                        const sel = formData.canal_entrada === value;
                        const isWpp = value === 'whatsapp';
                        return (
                          <button key={value} type="button"
                            onClick={() => setFormData({ ...formData, canal_entrada: value })}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.3rem',
                              padding: '0.3rem 0.65rem', borderRadius: '20px', fontSize: '0.78rem',
                              fontWeight: sel ? 600 : 400,
                              border: sel ? `2px solid ${isWpp ? '#25D366' : 'var(--primary,#2563eb)'}` : '2px solid #e2e8f0',
                              background: sel ? (isWpp ? '#dcfce7' : '#dbeafe') : '#fff',
                              color: sel ? (isWpp ? '#16a34a' : 'var(--primary,#2563eb)') : '#475569',
                              cursor: 'pointer', transition: 'all 0.15s',
                            }}
                          >
                            <Icon size={12} /> {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Classificação - Categorias e Tipos */}
                {categorias.length === 0 && (
                  <div style={{ background: '#fff8e1', border: '1px solid #f5d27a', color: '#7a5b0c', padding: '0.6rem 0.8rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.8rem' }}>
                    Nenhuma categoria de atendimento cadastrada. O atendimento será criado sem classificação; o administrador cadastra as categorias em Configurações de atendimento.
                  </div>
                )}
                {categorias.length > 0 && (() => {
                  const iconeMap = { FolderOpen, DollarSign, CheckCircle, Info, Tag };
                  const tiposSelecionados = tipos.filter((t) => t.categoria_id === parseInt(formData.categoria_id));
                  const requerSubtipo = tipos.find((t) => t.id === parseInt(formData.tipo_id))?.requer_subtipo;
                  const subtiposSelecionados = subtipos.filter((s) => s.tipo_id === parseInt(formData.tipo_id));

                  return (
                    <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid var(--gray-200)' }}>
                      <h4 style={{ marginBottom: '1rem', fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Tag size={14} />
                        Classificação do Atendimento *
                      </h4>

                      {/* Categoria — cards com ícone */}
                      <div style={{ marginBottom: tiposSelecionados.length ? '1rem' : 0 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.5rem' }}>
                          Categoria *
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {categorias.map((cat) => {
                            const CatIcon = iconeMap[cat.icone] || Tag;
                            const sel = formData.categoria_id === String(cat.id);
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => setFormData({ ...formData, categoria_id: String(cat.id), tipo_id: '', subtipo_id: '' })}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '0.45rem',
                                  padding: '0.45rem 0.9rem', borderRadius: '8px',
                                  border: sel ? '2px solid var(--primary, #2563eb)' : '2px solid #e2e8f0',
                                  background: sel ? 'var(--primary, #2563eb)' : '#fff',
                                  color: sel ? '#fff' : '#475569',
                                  fontWeight: sel ? 600 : 400, fontSize: '0.84rem',
                                  cursor: 'pointer', transition: 'all 0.15s',
                                  boxShadow: sel ? '0 2px 8px rgba(37,99,235,0.18)' : 'none',
                                }}
                              >
                                <CatIcon size={14} />
                                {cat.nome}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Tipo — chips pill */}
                      {tiposSelecionados.length > 0 && (
                        <div style={{ marginBottom: requerSubtipo ? '1rem' : 0 }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.5rem' }}>
                            Tipo *
                          </span>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {tiposSelecionados.map((tipo) => {
                              const sel = formData.tipo_id === String(tipo.id);
                              return (
                                <button
                                  key={tipo.id}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, tipo_id: String(tipo.id), subtipo_id: '' })}
                                  style={{
                                    padding: '0.35rem 0.8rem', borderRadius: '20px',
                                    border: sel ? '2px solid var(--primary, #2563eb)' : '2px solid #e2e8f0',
                                    background: sel ? '#dbeafe' : '#fff',
                                    color: sel ? 'var(--primary, #2563eb)' : '#475569',
                                    fontWeight: sel ? 600 : 400, fontSize: '0.82rem',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                  }}
                                >
                                  {tipo.nome}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Subtipo — chips pill roxo */}
                      {requerSubtipo && subtiposSelecionados.length > 0 && (
                        <div>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.5rem' }}>
                            Subtipo
                          </span>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {subtiposSelecionados.map((sub) => {
                              const sel = formData.subtipo_id === String(sub.id);
                              return (
                                <button
                                  key={sub.id}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, subtipo_id: String(sub.id) })}
                                  style={{
                                    padding: '0.35rem 0.8rem', borderRadius: '20px',
                                    border: sel ? '2px solid #7c3aed' : '2px solid #e2e8f0',
                                    background: sel ? '#ede9fe' : '#fff',
                                    color: sel ? '#7c3aed' : '#475569',
                                    fontWeight: sel ? 600 : 400, fontSize: '0.82rem',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                  }}
                                >
                                  {sub.nome}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Campos Dinâmicos do Setor */}
                {camposDinamicos.length > 0 && (
                  <div style={{ background: '#fff8e1', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #ffecb3' }}>
                    <h4 style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#f57c00' }}>
                      <ChevronDown size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                      Informações Adicionais
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      {camposDinamicos.map((campo) => (
                        <div key={campo.id} className="form-group" style={{ marginBottom: 0 }}>
                          <label>{campo.label}{campo.obrigatorio && ' *'}</label>
                          {renderCampoDinamico(campo)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Indicador WhatsApp - mostra que podera enviar mensagem apos criar */}
                {(selectedBeneficiario?.telefone_principal || selectedBeneficiario?.telefone) && (
                  <div style={{
                    background: '#f0faf8', padding: '0.75rem 1rem', borderRadius: '8px',
                    marginBottom: '1rem', border: '1px solid #b2dfdb',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: '#25D366', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>
                      <MessageCircle size={18} color="#fff" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                        WhatsApp disponivel
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#666' }}>
                        {selectedBeneficiario.telefone_principal || selectedBeneficiario.telefone}. Apos criar o atendimento, voce podera enviar mensagens pela aba WhatsApp.
                      </div>
                    </div>
                  </div>
                )}

                {/* ANEXOS — cofre de documentos do beneficiario (T7/T8).
                    INTERIM: visivel apenas para administradores ate a liberacao
                    oficial (produto neutro: gate por PERFIL, nunca por e-mail). */}
                {user?.perfil === 'administrador' && (
                <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', margin: '1rem 0', border: '1px solid var(--gray-200)' }}>
                  <h4 style={{ marginBottom: '0.3rem', fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Paperclip size={14} /> Anexos do beneficiário
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: '0.85rem' }}>
                    Documentos da pessoa (procuração, atestado de óbito, identidade…). Ficam disponíveis em todos os atendimentos dela.
                  </p>

                  {/* T8 — o que a pessoa ja tem (evita reanexar) */}
                  {docsExistentes.length > 0 && (
                    <div style={{ marginBottom: '0.85rem' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Já no cofre
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.4rem' }}>
                        {docsExistentes.map((d) => (
                          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.6rem', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
                            <FileText size={15} style={{ color: d.tipo_documento_cor || '#3498db', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.82rem', color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {d.nome_original}
                            </span>
                            {d.tipo_documento_nome && (
                              <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.12rem 0.5rem', borderRadius: '12px', color: d.tipo_documento_cor || '#3498db', background: `${d.tipo_documento_cor || '#3498db'}1a` }}>
                                {d.tipo_documento_nome}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dropzone */}
                  <label
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); adicionarArquivos(e.dataTransfer.files); }}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.3rem',
                      padding: '1.1rem', borderRadius: '8px', textAlign: 'center', cursor: 'pointer',
                      border: `2px dashed ${dragOver ? 'var(--brand, #20a490)' : '#cbd5e1'}`,
                      background: dragOver ? '#effbf8' : '#fff', color: '#64748b', fontSize: '0.83rem',
                    }}
                  >
                    <UploadCloud size={22} style={{ color: 'var(--brand, #20a490)' }} />
                    Arraste arquivos aqui ou clique para selecionar
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>PDF, JPG ou PNG até 10MB</span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={(e) => { adicionarArquivos(e.target.files); e.target.value = ''; }}
                    />
                  </label>

                  {/* Novos arquivos — "A classificar" ate escolher o tipo (obrigatorio) */}
                  {anexos.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
                      {anexos.map((a) => (
                        <div key={a.uid} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.65rem', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
                          <FileText size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.82rem', color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.file.name}
                          </span>

                          {a.status === 'enviado' ? (
                            <>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.76rem', fontWeight: 600, color: '#16a34a' }}>
                                <Check size={14} /> Anexado
                              </span>
                              <button type="button" title="Remover" onClick={() => removerAnexoEnviado(a.uid, a.docId)} style={iconBtn}>
                                <Trash2 size={15} />
                              </button>
                            </>
                          ) : a.status === 'enviando' ? (
                            <span style={{ fontSize: '0.76rem', color: '#64748b' }}>Enviando…</span>
                          ) : (
                            <>
                              <select
                                value={a.tipoId}
                                onChange={(e) => e.target.value && classificarEEnviar(a.uid, e.target.value)}
                                style={{
                                  fontSize: '0.8rem', padding: '0.3rem 0.5rem', borderRadius: '6px', maxWidth: '200px',
                                  border: a.status === 'erro' ? '1.5px solid #e11d48' : '1.5px solid #cbd5e1',
                                  background: '#fff', color: a.tipoId ? '#334155' : '#94a3b8',
                                }}
                              >
                                <option value="">{a.status === 'erro' ? 'Falhou, escolha o tipo' : 'A classificar…'}</option>
                                {tiposDoc.map((t) => (
                                  <option key={t.id} value={t.id}>{t.nome}</option>
                                ))}
                              </select>
                              <button type="button" title="Descartar" onClick={() => removerAnexoPendente(a.uid)} style={iconBtn}>
                                <X size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleCreateAtendimento(true)}
                    disabled={saving}
                  >
                    <CheckCircle size={16} />
                    {saving ? 'Salvando...' : 'Finalizar'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleCreateAtendimento(false)}
                    disabled={saving}
                  >
                    {saving ? 'Criando...' : 'Criar Atendimento'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Kanban;
